/**
 * OpsTeamPage — Executive-Director-only team management.
 * Add / edit / activate / deactivate any user without any code change.
 * Route: /ops/team  (executive_director only)
 */
import { useEffect, useState, useCallback } from 'react';
import { opsApi, ROLE_LABELS_AR, formatAuthError } from './OpsAuthContext';
import OpsLayout from './OpsLayout';

const emptyForm = () => ({
  email: '',
  name_ar: '',
  name_en: '',
  role: 'track_leader',
  title_ar: '',
  title_en: '',
  initial_password: 'Aafaq@2026',
});

const OpsTeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user object or null
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState('');
  const [banner, setBanner] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data } = await opsApi.get('/users');
    setTeam(data);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (msg) => { setBanner(msg); setTimeout(() => setBanner(''), 2500); };

  const createUser = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      await opsApi.post('/admin/users', form);
      setShowAdd(false);
      setForm(emptyForm());
      flash('تمت إضافة العضو ✅');
      refresh();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const patch = {
        name_ar: editing.name_ar,
        name_en: editing.name_en,
        role: editing.role,
        title_ar: editing.title_ar,
        title_en: editing.title_en,
      };
      await opsApi.patch(`/admin/users/${editing.id}`, patch);
      setEditing(null);
      flash('تم التحديث ✅');
      refresh();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const toggle = async (u) => {
    try {
      if (u.active) {
        await opsApi.delete(`/admin/users/${u.id}`);
        flash('تم إيقاف الحساب');
      } else {
        await opsApi.post(`/admin/users/${u.id}/reactivate`);
        flash('تم تفعيل الحساب');
      }
      refresh();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`كلمة المرور الجديدة للحساب ${u.email} (٦ أحرف كحد أدنى):`);
    if (pwd === null) return;
    if (pwd.length < 6) {
      window.alert('يجب أن تكون كلمة المرور ٦ أحرف على الأقل');
      return;
    }
    try {
      await opsApi.post(`/admin/users/${u.id}/password`, { new_password: pwd });
      flash(`تم تحديث كلمة مرور ${u.name_ar} ✅`);
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const hardDelete = async (u) => {
    const confirmed = window.confirm(
      `⚠️ سيتم حذف الحساب ${u.email} بشكل نهائي — لا يمكن التراجع.\n\nهل أنتِ متأكدة؟`,
    );
    if (!confirmed) return;
    try {
      await opsApi.delete(`/admin/users/${u.id}/permanent`);
      flash('تم الحذف النهائي');
      refresh();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  return (
    <OpsLayout title="فريق العمل" subtitle="Team Management">
      {banner && (
        <div className="mb-4 bg-[#E3EDDE] border border-[#B8CBA2] text-[#2C3814] rounded-xl px-4 py-2.5 text-[13px] font-['Noto_Kufi_Arabic']">
          {banner}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-[#5C6D49] font-['Noto_Kufi_Arabic']">
          إجمالي الأعضاء: {team.length}
        </p>
        <button
          data-testid="add-user-btn"
          onClick={() => { setShowAdd(true); setForm(emptyForm()); setErr(''); }}
          className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-5 py-2 rounded-lg hover:bg-[#163020]"
        >
          + عضو جديد
        </button>
      </div>

      {loading && <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>}

      {!loading && (
        <div className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F3F1E9] text-[#3D512D] font-['Noto_Kufi_Arabic']">
              <tr>
                <th className="text-start px-4 py-3">الاسم</th>
                <th className="text-start px-4 py-3">البريد</th>
                <th className="text-start px-4 py-3">الدور</th>
                <th className="text-start px-4 py-3">المسمى الوظيفي</th>
                <th className="text-start px-4 py-3">الحالة</th>
                <th className="text-start px-4 py-3">إجراء</th>
              </tr>
            </thead>
            <tbody className="font-['Noto_Kufi_Arabic']">
              {team.map((u) => (
                <tr key={u.id} className="border-t border-[#EFEAE1]" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 text-[#1F3D2B] font-semibold">{u.name_ar}</td>
                  <td className="px-4 py-3 text-[#5C6D49]" dir="ltr">{u.email}</td>
                  <td className="px-4 py-3 text-[#4A5D23]">{ROLE_LABELS_AR[u.role]}</td>
                  <td className="px-4 py-3 text-[#5C6D49]">{u.title_ar || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.active ? 'bg-[#DEE7CB] text-[#3F5021]' : 'bg-[#F0D0C9] text-[#7A2E22]'}`}>
                      {u.active ? 'نشط' : 'موقوف'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <button
                        data-testid={`edit-user-${u.email}`}
                        onClick={() => { setEditing({...u}); setErr(''); }}
                        className="text-[11px] text-[#4A5D23] hover:underline"
                      >
                        تعديل
                      </button>
                      <button
                        data-testid={`reset-password-${u.email}`}
                        onClick={() => resetPassword(u)}
                        className="text-[11px] text-[#573F7A] hover:underline"
                      >
                        كلمة المرور
                      </button>
                      <button
                        data-testid={`toggle-user-${u.email}`}
                        onClick={() => toggle(u)}
                        className="text-[11px] text-[#8A6A18] hover:underline"
                      >
                        {u.active ? 'إيقاف' : 'تفعيل'}
                      </button>
                      <button
                        data-testid={`delete-user-${u.email}`}
                        onClick={() => hardDelete(u)}
                        className="text-[11px] text-[#B84A3A] hover:underline"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showAdd || editing) && (
        <UserForm
          data={editing || form}
          onFieldChange={(field, val) => {
            if (editing) setEditing((u) => ({ ...u, [field]: val }));
            else setForm((f) => ({ ...f, [field]: val }));
          }}
          onCancel={() => { setShowAdd(false); setEditing(null); setErr(''); }}
          onSubmit={editing ? saveEdit : createUser}
          isEdit={!!editing}
          err={err}
        />
      )}
    </OpsLayout>
  );
};

const UserForm = ({ data, onFieldChange, onCancel, onSubmit, isEdit, err }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" dir="rtl">
    <form
      data-testid="user-form"
      onSubmit={onSubmit}
      className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
    >
      <h3 className="font-['Noto_Kufi_Arabic'] text-[18px] font-bold text-[#1F3D2B] mb-4">
        {isEdit ? 'تعديل عضو' : 'إضافة عضو جديد'}
      </h3>
      <div className="space-y-3">
        <input
          data-testid="uf-email"
          type="email"
          required
          dir="ltr"
          disabled={isEdit}
          value={data.email || ''}
          onChange={(e) => onFieldChange('email', e.target.value)}
          placeholder="البريد الإلكتروني"
          className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 disabled:bg-gray-50 text-start"
        />
        <input
          data-testid="uf-name-ar"
          required
          value={data.name_ar || ''}
          onChange={(e) => onFieldChange('name_ar', e.target.value)}
          placeholder="الاسم بالعربية"
          className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
        />
        <input
          value={data.name_en || ''}
          onChange={(e) => onFieldChange('name_en', e.target.value)}
          placeholder="Name (English) — optional"
          className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
        />
        <select
          data-testid="uf-role"
          value={data.role}
          onChange={(e) => onFieldChange('role', e.target.value)}
          className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
        >
          <option value="executive_director">المدير التنفيذي</option>
          <option value="operations_manager">مسؤول التشغيل</option>
          <option value="track_leader">قائد مسار</option>
          <option value="member">عضو فريق</option>
        </select>
        <input
          value={data.title_ar || ''}
          onChange={(e) => onFieldChange('title_ar', e.target.value)}
          placeholder="المسمى الوظيفي بالعربية"
          className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
        />
        {!isEdit && (
          <input
            data-testid="uf-password"
            required
            value={data.initial_password || ''}
            onChange={(e) => onFieldChange('initial_password', e.target.value)}
            placeholder="كلمة المرور المؤقتة"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2"
          />
        )}
      </div>
      {err && <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic'] mt-3">{err}</p>}
      <div className="flex gap-2 mt-5">
        <button
          type="submit"
          data-testid="uf-submit"
          className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-5 py-2 rounded-lg hover:bg-[#163020]"
        >
          {isEdit ? 'حفظ التعديل' : 'إضافة'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-['Noto_Kufi_Arabic'] text-[#3D512D] px-5 py-2 rounded-lg hover:bg-[#F3F1E9]"
        >
          إلغاء
        </button>
      </div>
    </form>
  </div>
);

export default OpsTeamPage;
